import { format } from "date-fns";
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getAllBillingInvoices, getAppointmentsByDateRange, getDoctors,
  recordInvoicePayment, getBillingRevenueSummary,
} from '@/lib/api';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';
import { usePageTitle } from "@/hooks/usePageTitle";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
const MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card', cheque: 'cheque',
  netbanking: 'netbanking', 'bank transfer': 'netbanking',
  insurance: 'other', other: 'other', advance: 'advance',
};

const MODE_LABEL: Record<string, string> = {
  cash: 'Cash', upi: 'UPI', card: 'Card',
  netbanking: 'Net Banking', cheque: 'Cheque',
  advance: 'Advance', other: 'Other',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FrontDeskReportsPage() {
  usePageTitle('Reports');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const clinicId = currentUser?.clinic_id ?? '';

  const [invoices, setInvoices] = useState<any[]>([]);
  // Money taken today in every mode — deposits and payments on earlier bills too (the day's cash-up)
  const [receivedToday, setReceivedToday] = useState<number | null>(null);
  const [refundedToday, setRefundedToday] = useState(0);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collectInvoice, setCollectInvoice] = useState<any>(null);

  const today = format(new Date(), "yyyy-MM-dd");

  const load = async () => {
    if (!clinicId) return;
    try {
      const [invData, aptData, docData, received] = await Promise.all([
        can('billing:read') ? getAllBillingInvoices(clinicId, { dateFrom: today, dateTo: today }) : Promise.resolve([]),
        getAppointmentsByDateRange(today, today, clinicId),
        getDoctors(),
        can('billing:read') ? getBillingRevenueSummary(clinicId, 0).catch(() => null) : Promise.resolve(null),
      ]);

      const invArr = Array.isArray(invData) ? invData : (invData as any)?.invoices ?? [];
      // A cancelled bill isn't billed
      setInvoices(invArr.filter((inv: any) => inv.status !== 'cancelled' && inv.payment_status !== 'cancelled'));
      setReceivedToday(Array.isArray(received) ? received.reduce((n: number, r: any) => n + Number(r.collected_amount || 0), 0) : null);
      setRefundedToday(Array.isArray(received) ? received.reduce((n: number, r: any) => n + Number(r.refunded_amount || 0), 0) : 0);
      setAppointments(Array.isArray(aptData) ? aptData : []);
      setDoctors(Array.isArray(docData) ? docData : []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load reports', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [clinicId]);

  const handleRefresh = () => { setRefreshing(true); load(); };

  // ── Billing stats ────────────────────────────────────────────────────────

  const billing = useMemo(() => {
    const totalBilled    = invoices.reduce((s, inv) => s + Number(inv.total_amount || 0), 0);
    // Paid over a bill's total (earlier dues settled through it) counts as paid in full, as on the Billing page
    const collected      = invoices.reduce((s, inv) => s + Math.min(Number(inv.paid_amount || 0), Number(inv.total_amount || 0)), 0);
    const outstanding    = totalBilled - collected;
    const paidCount      = invoices.filter(inv => inv.payment_status === 'paid').length;
    const partialCount   = invoices.filter(inv => inv.payment_status === 'partial').length;
    const unpaidCount    = invoices.filter(inv => inv.payment_status === 'unpaid').length;
    return { totalBilled, collected, outstanding, paidCount, partialCount, unpaidCount, total: invoices.length };
  }, [invoices]);

  // ── Doctor footfall ──────────────────────────────────────────────────────

  const footfall = useMemo(() => {
    const map: Record<string, { scheduled: number; completed: number; cancelled: number; total: number }> = {};
    appointments.forEach(apt => {
      const did = apt.doctor_id ?? 'unknown';
      if (!map[did]) map[did] = { scheduled: 0, completed: 0, cancelled: 0, total: 0 };
      map[did].total++;
      if (apt.status === 'completed' || apt.status === 'checked_in' || apt.status === 'in_progress') map[did].completed++;
      else if (apt.status === 'cancelled' || apt.status === 'no_show') map[did].cancelled++;
      else map[did].scheduled++;
    });
    return Object.entries(map).map(([doctorId, counts]) => ({
      doctorId,
      doctorName: doctors.find(d => d.id === doctorId)?.name ?? 'Unknown',
      ...counts,
    })).sort((a, b) => b.total - a.total);
  }, [appointments, doctors]);

  // ── Outstanding invoices ─────────────────────────────────────────────────

  const unpaidInvoices = useMemo(() =>
    invoices
      .filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'partial')
      .sort((a, b) => Number(b.total_amount) - Number(a.total_amount)),
  [invoices]);

  // ── Collect payment ──────────────────────────────────────────────────────

  const handleCollect = async (amount: number, method: string) => {
    if (!collectInvoice) return;
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_mode: (MODE_MAP[method?.toLowerCase()] ?? 'cash') as PaymentMode,
        payment_date: new Date().toISOString(),
      });
      toast({ title: 'Payment recorded', description: `${fmt(amount)} collected` });
      setCollectInvoice(null);
      load();
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="prescription-form-container flex items-center justify-center min-h-[400px]" aria-busy="true" aria-label="Loading summary">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const seenTotal = appointments.filter(a => ['completed', 'checked_in', 'in_progress'].includes(a.status)).length;
  const cancelledTotal = appointments.filter(a => ['cancelled', 'no_show'].includes(a.status)).length;
  const figure = (value: React.ReactNode) => <span className="font-semibold tabular-nums text-foreground">{value}</span>;
  const statusParts: React.ReactNode[] = [
    <span key="date">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</span>,
    ...(can('billing:read') ? [<span key="inv">{figure(billing.total)} invoice{billing.total !== 1 ? 's' : ''}</span>] : []),
    <span key="apt">{figure(appointments.length)} appointment{appointments.length !== 1 ? 's' : ''}</span>,
  ];
  const num = (n: number) => <span className={cn('font-mono tabular-nums', n === 0 ? 'text-muted-foreground' : 'text-foreground')}>{n}</span>;

  return (
    <div className="prescription-form-container space-y-5">

      {/* Header — back, title, one status line */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="sm" onClick={() => navigate('/frontdesk')} aria-label="Back to front desk" className="w-8 px-0 flex-shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground">Today's summary</h1>
            <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
              {statusParts.map((part, i) => (
                <React.Fragment key={i}>{i > 0 && <span aria-hidden>·</span>}{part}</React.Fragment>
              ))}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-1.5" aria-label="Refresh">
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* ── Billing ─────────────────────────────────────────────────────────── */}
      {can('billing:read') ? (
        <section aria-label="Billing" className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center h-11 px-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Billing</h2>
          </div>
          {/* One row of figures; the grid lines come from the 1px gap over the border colour */}
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
            {[
              { label: 'Billed', value: fmt(billing.totalBilled), sub: `${billing.total} invoice${billing.total !== 1 ? 's' : ''}` },
              { label: 'Paid', value: fmt(billing.collected), sub: billing.totalBilled > 0 ? `${Math.round((billing.collected / billing.totalBilled) * 100)}% of billed` : undefined },
              { label: 'Outstanding', value: fmt(billing.outstanding), due: billing.outstanding > 0, sub: unpaidInvoices.length ? `${unpaidInvoices.length} invoice${unpaidInvoices.length !== 1 ? 's' : ''}` : undefined },
              // Net of refunds — what should be in hand at the cash-up
              { label: 'Received today', value: receivedToday == null ? '—' : fmt(receivedToday - refundedToday), sub: refundedToday > 0 ? `${fmt(receivedToday ?? 0)} in · ${fmt(refundedToday)} out` : 'all modes · deposits included' },
            ].map(f => (
              <div key={f.label} className="bg-card px-4 py-3 min-w-0">
                <dt className="text-xs text-muted-foreground">{f.label}</dt>
                <dd className="mt-0.5">
                  <span className={cn(
                    'font-mono text-xl font-semibold tabular-nums',
                    f.due ? 'rounded bg-highlight px-1.5 text-highlight-foreground' : 'text-foreground',
                  )}>
                    {f.value}
                  </span>
                  {f.sub && <span className="block text-xs text-muted-foreground">{f.sub}</span>}
                </dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-t border-border text-xs text-muted-foreground">
            {billing.total === 0 ? (
              <span>No invoices created today.</span>
            ) : (
              <>
                <StatusChip tone="success">Paid {billing.paidCount}</StatusChip>
                <StatusChip tone="accent">Partial {billing.partialCount}</StatusChip>
                <StatusChip tone="warning">Unpaid {billing.unpaidCount}</StatusChip>
              </>
            )}
          </div>
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">You don't have permission to see billing figures.</p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">

        {/* ── OPD footfall ────────────────────────────────────────────────────── */}
        <section aria-label="OPD footfall" className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center h-11 px-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">OPD footfall</h2>
          </div>
          {footfall.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">No appointments today.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="h-8 border-b border-border bg-muted/40 text-2xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="px-4 text-left font-medium">Doctor</th>
                  <th scope="col" className="px-2 sm:px-3 text-right font-medium">Booked</th>
                  <th scope="col" className="px-2 sm:px-3 text-right font-medium">Seen</th>
                  <th scope="col" className="pl-2 sm:pl-3 pr-4 text-right font-medium">Cancelled</th>
                </tr>
              </thead>
              <tbody>
                {footfall.map(row => (
                  <tr key={row.doctorId} className="h-10 border-b border-divider last:border-0">
                    <td className="px-4 py-2 w-full text-foreground">{drName(row.doctorName)}</td>
                    <td className="px-2 sm:px-3 text-right">{num(row.total)}</td>
                    <td className="px-2 sm:px-3 text-right">{num(row.completed)}</td>
                    <td className="pl-2 sm:pl-3 pr-4 text-right">{num(row.cancelled)}</td>
                  </tr>
                ))}
              </tbody>
              {footfall.length > 1 && (
                <tfoot>
                  <tr className="h-10 border-t border-border bg-muted/40 font-semibold">
                    <td className="px-4 text-foreground">Total</td>
                    <td className="px-2 sm:px-3 text-right">{num(appointments.length)}</td>
                    <td className="px-2 sm:px-3 text-right">{num(seenTotal)}</td>
                    <td className="pl-2 sm:pl-3 pr-4 text-right">{num(cancelledTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </section>

        {/* ── Outstanding today ─────────────────────────────────────────────── */}
        {can('billing:read') && (
          <section aria-label="Outstanding today" className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center h-11 px-4 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">
                Outstanding today
                {unpaidInvoices.length > 0 && <span className="font-normal text-muted-foreground tabular-nums"> · {unpaidInvoices.length}</span>}
              </h2>
            </div>
            {unpaidInvoices.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground">Nothing outstanding from today's bills.</p>
            ) : (
              <ul>
                {unpaidInvoices.map(inv => {
                  const due = Number(inv.total_amount) - Number(inv.paid_amount || 0);
                  const partial = inv.payment_status === 'partial';
                  return (
                    <li key={inv.id} className="flex items-center gap-3 px-4 py-2 min-h-12 border-b border-divider last:border-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate capitalize">{inv.patient_name ?? inv.invoice_number}</p>
                        <p className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-mono truncate">{inv.invoice_number}</span>
                          <StatusChip tone={partial ? 'accent' : 'warning'}>{partial ? 'Partial' : 'Unpaid'}</StatusChip>
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-mono text-sm font-semibold tabular-nums text-highlight-foreground">{fmt(due)}</p>
                        {partial && <p className="text-xs text-muted-foreground tabular-nums">of {fmt(Number(inv.total_amount))}</p>}
                      </div>
                      {can('billing:write') && (
                        <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => setCollectInvoice(inv)}>
                          Collect
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>

      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollect}
        loading={false}
      />
    </div>
  );
}
